// @givmo/app-admin — Support page (07-1.5a). Static contact surface, no API.
//
// A parity rebuild of the Flutter `support` feature: a single line directing the admin to email
// support, with the address bolded and a copy-to-clipboard button. No mailto link, no form.
//
// The root keeps `data-testid="page-support"` — the route-guard test asserts it.

import { useState } from 'react';
import {
  Card,
  CardBody,
  Icon,
  IconButton,
  PageHeader,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Check, Copy, LifeBuoy, Mail } from 'lucide-react';

const SUPPORT_EMAIL = 'support@givmocharitable.org';

export function SupportPage() {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      toast.success('Email copied to clipboard');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy the email. Please copy it manually.');
    }
  };

  return (
    <div
      className="mx-auto flex w-full max-w-[960px] flex-col gap-20 px-24 py-24"
      {...testIdProp('page-support')}
    >
      <PageHeader
        title="Support"
        subtitle="Reach the Givmo team for any questions, comments, or concerns."
      />

      <Card>
        <CardBody className="flex flex-col gap-16">
          <div className="flex items-center gap-12">
            <span className="flex size-[40px] items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <Icon icon={LifeBuoy} size={20} aria-hidden />
            </span>
            <div className="flex flex-col">
              <span className="font-sans text-s15 font-w700 tracking-givmo text-text-strong">
                Contact support
              </span>
              <span className="font-sans text-s13 tracking-normal text-text-tertiary">
                Please email us with any questions, comments, or concerns at the address below.
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-12 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12">
            <Icon icon={Mail} size={16} className="text-icon-muted" aria-hidden />
            <span
              className="font-sans text-s15 font-w600 tracking-givmo text-text-strong"
              {...testIdProp('support-email')}
            >
              {SUPPORT_EMAIL}
            </span>
            <span className="ml-auto">
              <IconButton
                aria-label="Copy support email"
                tone="ghost"
                onClick={() => void copyEmail()}
                {...testIdProp('support-copy')}
              >
                <Icon icon={copied ? Check : Copy} size={18} aria-hidden />
              </IconButton>
            </span>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
